import {Field, Form, Formik} from 'formik'
import React, {useEffect, useState} from 'react'
import {SelectInput} from '../../../widgets/components/Input/SelectInput'
import {ToolTipUI} from '../../../widgets/components/UI/ToolTipUI'
import {useIntl} from 'react-intl'
import deleteIcon from '../../../../../media/stakeholders/ic-delete.svg'
import {useThemeMode} from '../../../../../_metronic/partials'
import {useAuth} from '../../../auth'
import {createSettings, deleteSettings, getClassification} from '../../core/_requests'
import {toast} from 'react-toastify'
import {periodOptions, viewableFileTypes} from '../../../../core/_constants'
import Skeleton from 'react-loading-skeleton'
import * as Yup from 'yup'
import {SettingsInputPeriod} from '../../../widgets/components/Input/SettingsInputPeriod'

const Retention = ({fileLimits2, setFileLimits2, aiEmailData, aiEmailLoading}: any) => {
  const {formatMessage} = useIntl()
  const {mode} = useThemeMode()
  const {companyId} = useAuth()
  const [departmentListLoading, setDepartmentListLoading] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [departmentList, setDepartmentList] = useState<any>([])
  const [disabledDepartmentTypes, setDisabledDepartmentTypes] = useState<string[]>([])
  const [disabledFileTypes, setDisabledFileTypes] = useState<string[]>([])
  const [fileLimits, setFileLimits] = useState([
    {name: '', type: 'DEPARTMENT', retentionValue: '', retentionPeriod: ''},
  ])

  const validationSchema = Yup.object().shape({
    islegalHoldExtension: Yup.boolean(),

    extensionYears: Yup.number()
      .nullable()
      .when('islegalHoldExtension', {
        is: true,
        then: (schema) =>
          schema
            .required('Extension Years is required')
            .positive("Extension Years cann't be negative or 0"),
        otherwise: (schema) => schema.nullable(),
      }),

    extensionPeriod: Yup.string()
      .nullable()
      .when('islegalHoldExtension', {
        is: true,
        then: (schema) => schema.required('Extension Period is required'),
        otherwise: (schema) => schema.nullable(),
      }),
    defaultRetentionValue: Yup.number()
      .required('Default Retention Period is required')
      .nullable()
      .min(1, "Default Retention Period cann't be negative or 0"),
    // Dynamically add validation for fields based on fileLimits
    ...(fileLimits || []).reduce(
      (acc, _, index) => {
        acc[`retentionValue${index + 1}`] = Yup.number()
          .required(formatMessage({id: 'Retention Value is required'}))
          .positive(formatMessage({id: "Retention Value cann't be negative or 0"}))
        acc[`retentionPeriod${index + 1}`] = Yup.string().required(
          formatMessage({id: 'Retention Period is required'})
        )
        acc[`departmentType${index + 1}`] = Yup.string().nullable() // Adjust as needed
        return acc
      },
      {} as Record<string, any> // Explicitly type the accumulator for spread
    ),

    // Dynamically add validation for fields based on fileLimits2
    ...(fileLimits2 || []).reduce(
      (acc: any, _: any, index: number) => {
        acc[`fileRetentionValue${index + 1}`] = Yup.number()
          .required(formatMessage({id: 'Retention Value is required'}))
          .positive(formatMessage({id: "Retention Value cann't be negative or 0"}))
        acc[`fileRetentionPeriod${index + 1}`] = Yup.string().required(
          formatMessage({id: 'Retention Period is required'})
        )
        acc[`fileType${index + 1}`] = Yup.string().nullable() // Adjust as needed
        return acc
      },
      {} as Record<string, any> // Explicitly type the accumulator for spread
    ),
  })

  useEffect(() => {
    const updatedFilteredTypes = departmentList
      .filter((type: any) => !fileLimits.some((limit) => limit.name === type.name))
      .map((type: any) => type.name)

    setDisabledDepartmentTypes(updatedFilteredTypes)
  }, [fileLimits]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updatedFilteredTypes = viewableFileTypes.filter(
      (type: string) => !fileLimits2.some((limit: any) => limit.name === type)
    )

    setDisabledFileTypes(updatedFilteredTypes)
  }, [fileLimits2])

  const addFileLimit = () => {
    setFileLimits([
      ...fileLimits,
      {name: '', type: 'DEPARTMENT', retentionValue: '', retentionPeriod: ''},
    ])
  }
  const deleteFileLimit = (index: any) => {
    const newLimits = fileLimits.filter((_, i) => i !== index)
    setFileLimits(newLimits)
  }

  const addFileLimit2 = () => {
    setFileLimits2([
      ...fileLimits2,
      {name: '', type: 'FILE', retentionValue: '', retentionPeriod: ''},
    ])
  }
  const deleteFileLimit2 = (index: any, id: string) => {
    const newLimits2 = fileLimits2.filter((_: any, i: number) => i !== index)
    setFileLimits2(newLimits2)
    if (!companyId || !id) return
    deleteSettings(companyId, id, false)
  }

  const initialValues = {
    defaultRetentionValue: aiEmailData?.retentionValue || '',
    defaultRetentionPeriod: aiEmailData?.retentionPeriod || '',
    islegalHoldExtension: aiEmailData?.retentionExtension || false,
    extensionYears: aiEmailData?.maxRetentionValue || '',
    extensionPeriod: aiEmailData?.maxRetentionPeriod || '',

    ...fileLimits?.reduce((acc: Record<string, string | number>, limit, index) => {
      acc[`departmentType${index + 1}`] = limit.name || ''
      acc[`retentionValue${index + 1}`] = limit.retentionValue || ''
      acc[`retentionPeriod${index + 1}`] = limit.retentionPeriod || 'Days'
      return acc
    }, {}),
    ...fileLimits2?.reduce((acc: Record<string, string | number>, limit: any, index: any) => {
      acc[`fileType${index + 1}`] = limit.name || ''
      acc[`fileRetentionValue${index + 1}`] = limit.retentionValue || ''
      acc[`fileRetentionPeriod${index + 1}`] = limit.retentionPeriod || 'Days'
      return acc
    }, {}),
  }

  const getDepartmentList = async () => {
    if (!companyId) return
    const type = 'LevelOne'
    const All = true
    try {
      setDepartmentListLoading(true)
      const {
        data: {data, success, errors},
      } = await getClassification(companyId, type, All)
      if (success) {
        const departmentData =
          data?.data?.map((item: any) => ({
            name: item?.name || '',
            type: 'DEPARTMENT',
            retentionValue:
              item?.retentionValue === null
                ? aiEmailData?.retentionValue
                : String(item?.retentionValue),
            retentionPeriod:
              item?.retentionPeriod === null
                ? aiEmailData?.retentionPeriod
                : String(item?.retentionPeriod),
          })) || []

        const transformedData = data?.data?.map((item: any) => ({
          id: item.departmentId,
          name: item.name,
          value: item.name,
        }))
        setFileLimits(departmentData)
        setDepartmentList(transformedData)
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
    } finally {
      setDepartmentListLoading(false)
    }
  }

  useEffect(() => {
    getDepartmentList()
  }, [aiEmailData]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values: any, {resetForm}: any) => {
    if (!companyId) return   
    setLoading(true)
    const payload = {
      retentionValue: values?.defaultRetentionValue,
      retentionPeriod: values?.defaultRetentionPeriod,
      departments: fileLimits,
      files: fileLimits2,
      retentionExtension: values?.islegalHoldExtension,
      maxRetentionValue: parseInt(values?.extensionYears),
      maxRetentionPeriod: values?.extensionPeriod,
    }
    try {
      const {
        data: {success, errors},
      } = await createSettings(companyId, payload)
      if (success) {
        toast.success('Setting saved successfully')
      } else {
        toast.error(errors.ErrorMessage)
      }
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnChange
          validateOnMount
        >
          {(formik: any) => {
            return (
              <Form>
                <div className='row'>
                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <div className='d-flex flex-row my-3'>
                        <div className='w-25 me-5'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                        <div className='w-75'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      </div>
                    ) : (
                      <SettingsInputPeriod
                        label={formatMessage({
                          id: 'Default Retention Period for All Records',
                        })}
                        toolTipText={formatMessage({
                          id: 'Set the retention period for all files and records if no specific department or file type retention policy is applied.',
                        })}
                        fieldName='defaultRetentionValue'
                        placeholder={''}
                        placeholderSelect={''}
                        formik={formik}
                        isTooltipForModal={true}
                        options={periodOptions}
                        selectOptionFieldName='defaultRetentionPeriod'
                      />
                    )}
                  </div>
                  <div className='d-flex align-items-center mb-8'>
                    <p className='fw-semibold m-0'>
                      {formatMessage({id: 'Default Retention Period by Department'})}
                    </p>
                    <ToolTipUI
                      tooltipText={formatMessage({
                        id: `Define custom retention periods for files based on department or role. Files will be retained according to the specific retention period set for each department or role.`,
                      })}
                    />
                  </div>

                  {departmentListLoading ? (
                    <div className='d-flex flex-row mb-3'>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    </div>
                  ) : (
                    fileLimits.map((limit, index) => (
                      <>
                        <div className='col-md-5 col-12'>
                          <SelectInput
                            label={formatMessage({id: 'Department/Role-Based Retention'})}
                            fieldName={`departmentType${index + 1}`}
                            placeholder={''}
                            formik={formik}
                            toolTipText={formatMessage({
                              id: 'Select the Department for which you want to define the specific retention period.',
                            })}
                            onChange={(event) => {
                              const value = event.target.value
                              setFileLimits((prevLimits) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  name: value,
                                }
                                return newLimits
                              })
                            }}
                            options={departmentList}
                            disabledFileTypes={disabledDepartmentTypes}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <SettingsInputPeriod
                            label={formatMessage({
                              id: 'Retention Period',
                            })}
                            toolTipText={formatMessage({
                              id: `The length of time files will be retained for the selected department or role before they are archived or deleted.`,
                            })}
                            fieldName={`retentionValue${index + 1}`}
                            placeholder={''}
                            placeholderSelect={''}
                            formik={formik}
                            isTooltipForModal={true}
                            options={periodOptions}
                            selectOptionFieldName={`retentionPeriod${index + 1}`}
                            onChange={(event:any) => {
                              
                              const value = event.value
                              setFileLimits((prevLimits) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  retentionPeriod: value,
                                }
                                return newLimits
                              })
                            }}
                            onInputChange={(event:any) => {
                              const value = event.target.value
                              setFileLimits((prevLimits) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  retentionValue: value,
                                }
                                return newLimits
                              })
                            }}
                          />
                        </div>
                        <div className='col-1 col-md-1 align-self-start mt-9  mb-md-0'>
                          <img
                            src={deleteIcon}
                            alt='delete'
                            className='cursor-pointer'
                            width={32}
                            height={32}
                            onClick={() => deleteFileLimit(index)}
                          />
                        </div>
                      </>
                    ))
                  )}

                  <div>
                    <button
                      className='btn btn-primary'
                      disabled={departmentList.length === fileLimits.length}
                      type='button'
                      onClick={addFileLimit}
                    >
                      {formatMessage({id: '+ Add More Department/Role-Based Retention'})}
                    </button>
                    <ToolTipUI
                      tooltipText={formatMessage({
                        id: `Click to add additional departments or roles with custom retention periods.`,
                      })}
                    />
                  </div>
                  <div className='d-flex align-items-center mt-8 mb-5'>
                    <p className='fw-semibold m-0'>
                      {formatMessage({id: 'Default Retention Period for Different File Types'})}
                    </p>
                    <ToolTipUI
                      tooltipText={formatMessage({
                        id: `Set retention periods for specific file types. This will override the default retention period if a retention policy is defined for a particular file type.`,
                      })}
                    />
                  </div>
                  <div className='col-12 col-md-12'>
                    <div
                      className='p-2 rounded mb-8'
                      style={
                        mode === 'dark'
                          ? {background: '#6C4F2C', border: '2px dashed #FFC700'}
                          : {background: '#FFF8DD', border: '2px dashed #FFC700'}
                      }
                    >
                      <p className='m-0 '>
                        <span className='fw-bold'>{formatMessage({id: 'Note: '})}</span>
                        {formatMessage({
                          id: 'In case of a retention period conflict, the higher limit will apply. For example, if the department-based retention period is set to 10 Years but the retention period for PDF files is 7 Years, the final retention period for for PDFs in the Finance department will be 10 Years.',
                        })}
                      </p>
                    </div>
                  </div>

                  {aiEmailLoading ? (
                    <div className='d-flex flex-row mb-3'>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    </div>
                  ) : (
                    fileLimits2.map((limit: any, index: any) => (
                      <>
                        <div className='col-md-5 col-12'>
                          <SelectInput
                            label={formatMessage({id: 'File Type-Based Retention'})}
                            fieldName={`fileType${index + 1}`}
                            placeholder={''}
                            formik={formik}
                            toolTipText={formatMessage({
                              id: 'Select the file type (e.g., Word, PDF, Excel) to define a custom retention period for files of that type.',
                            })}
                            onChange={(event) => {
                              const value = event.target.value
                              setFileLimits2((prevLimits: any) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  name: value,
                                }
                                return newLimits
                              })
                            }}
                            options={viewableFileTypes}
                            disabledFileTypes={disabledFileTypes}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <SettingsInputPeriod
                            label={formatMessage({
                              id: 'Retention Period',
                            })}
                            toolTipText={formatMessage({
                              id: `The duration for which files of the selected type will be retained before being archived or deleted.`,
                            })}
                            fieldName={`fileRetentionValue${index + 1}`}
                            placeholder={''}
                            placeholderSelect={''}
                            formik={formik}
                            isTooltipForModal={true}
                            options={periodOptions}
                            selectOptionFieldName={`fileRetentionPeriod${index + 1}`}
                            onChange={(event:any) => {
                              
                              const value = event.value
                              setFileLimits2((prevLimits:any) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  retentionPeriod: value,
                                }
                                return newLimits
                              })
                            }}
                            onInputChange={(event:any) => {
                              const value = event.target.value
                              setFileLimits2((prevLimits:any) => {
                                const newLimits = [...prevLimits]
                                newLimits[index] = {
                                  ...newLimits[index],
                                  retentionValue: value,
                                }
                                return newLimits
                              })
                            }}
                          />
                        </div>
                        <div className='col-1 col-md-1 align-self-start mt-9 mb-md-0'>
                          <img
                            src={deleteIcon}
                            alt='delete'
                            className='cursor-pointer'
                            width={32}
                            height={32}
                            onClick={() => deleteFileLimit2(index, limit.id)}
                          />
                        </div>
                      </>
                    ))
                  )}

                  <div>
                    <button className='btn btn-primary' type='button' onClick={addFileLimit2}>
                      {formatMessage({id: '+ Add More File Type-Based Retention'})}
                    </button>
                    <ToolTipUI
                      tooltipText={formatMessage({
                        id: `Click to add additional file types with custom retention periods.`,
                      })}
                    />
                  </div>
                  <div className='d-flex align-items-center mt-8 mb-5'>
                    <p className='fw-semibold m-0'>
                      {formatMessage({id: 'Flag for Retention Extension'})}
                    </p>
                  </div>
                  <div className='col-md-12 col-12 mb-8'>
                    <div className='form-check form-check-custom form-check-solid form-check-primary form-switch'>
                      <label className='togglebtn mx-0'>
                        <Field
                          className='togglebtn__input'
                          name='islegalHoldExtension'
                          type='checkbox'
                          formik={formik}
                          onChange={(event: any) => {
                            formik.handleChange(event)
                          }}
                        />
                        <div className='togglebtn__fill'></div>
                      </label>
                      <div className='d-flex flex-column justify-content-end mt-2'>
                        <div className='d-flex'>
                          <div>
                            <label
                              className='form-check-label fw-semibold font-size-13 title-notification'
                              data-bs-toggle='tooltip'
                            >
                              {formatMessage({id: 'Enable users to extend the retention period'})}
                            </label>
                          </div>
                        </div>
                        <label
                          className='form-check-label text-clr58 fw-normal fs-7'
                          data-bs-toggle='tooltip'
                        >
                          {formatMessage({
                            id: 'Turn this on to allow users to extend the retention period for individual files beyond the default policy.',
                          })}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-6 col-12'>
                      <SettingsInputPeriod
                        label={formatMessage({id: 'Maximum Extension Period Users Can Extend'})}
                        toolTipText={formatMessage({
                          id: `Set the maximum duration for which users can extend the retention period for individual files.`,
                        })}
                        fieldName='extensionYears'
                        placeholder={''}
                        placeholderSelect={''}
                        formik={formik}
                        isTooltipForModal={true}
                        options={periodOptions}
                        selectOptionFieldName='extensionPeriod'
                        disabled={!formik.values.islegalHoldExtension}
                      />
                  </div>
                  <div className='d-flex justify-content-end'>
                    <button
                      type='submit'
                      className='btn bg-primary text-white custon-button-property border border-1 mb-md-4 mb-0 px-6 py-4 text-center w-100 w-md-auto order-1 order-md-2'
                      disabled={!formik.isValid || loading}
                    >
                      {!loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({id: 'Save Changes'})}
                        </span>
                      )}
                      {loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({id: 'Please wait...'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </>
    </div>
  )
}

export default Retention